import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  browserSupportsWebAuthn,
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
} from '@simplewebauthn/browser';

interface LoginFormProps {
  onLoginSuccess: (user: { username: string; roles: string[]; id?: string, _id?: string }) => void;
}

const LoginForm: React.FC<LoginFormProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [focusedInput, setFocusedInput] = useState<string | null>(null);
  const passkeySupported = typeof window !== 'undefined' && browserSupportsWebAuthn();
  const onLoginSuccessRef = useRef(onLoginSuccess);
  onLoginSuccessRef.current = onLoginSuccess;

  // Password is primary only when the browser cannot use passkeys
  useEffect(() => {
    if (!passkeySupported) {
      setShowPasswordForm(true);
    }
  }, [passkeySupported]);

  const verifyPasskeyAssertion = useCallback(async (assertion: unknown, attemptId: string) => {
    const verifyRes = await fetch('/api/auth/passkey/login/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ attemptId, response: assertion }),
    });
    const verifyData = await verifyRes.json();
    if (!verifyRes.ok) {
      throw new Error(verifyData.error || 'Passkey login failed');
    }
    onLoginSuccessRef.current(verifyData.user);
  }, []);

  const fetchPasskeyOptions = useCallback(async (withUsername?: string) => {
    const optionsRes = await fetch('/api/auth/passkey/login/options', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(withUsername ? { username: withUsername } : {}),
    });
    const optionsData = await optionsRes.json();
    if (!optionsRes.ok) {
      throw new Error(optionsData.error || 'Could not start passkey login');
    }
    const { attemptId, ...optionsJSON } = optionsData;
    if (!attemptId) {
      throw new Error('Invalid passkey challenge from server');
    }
    return { attemptId: attemptId as string, optionsJSON };
  }, []);

  // Conditional UI: passkey appears in browser autofill when focusing username
  useEffect(() => {
    if (!passkeySupported) return;

    let cancelled = false;

    (async () => {
      try {
        const autofillOk = await browserSupportsWebAuthnAutofill();
        if (!autofillOk || cancelled) return;

        const { attemptId, optionsJSON } = await fetchPasskeyOptions();
        if (cancelled) return;

        const assertion = await startAuthentication({
          optionsJSON,
          useBrowserAutofill: true,
        });
        if (cancelled) return;

        setPasskeyLoading(true);
        setError('');
        await verifyPasskeyAssertion(assertion, attemptId);
      } catch (err: unknown) {
        const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
        if (name !== 'NotAllowedError' && name !== 'AbortError' && !cancelled) {
          console.debug('[passkey] conditional UI ended:', err);
        }
      } finally {
        if (!cancelled) setPasskeyLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      try {
        WebAuthnAbortService.cancelCeremony();
      } catch {
        /* ignore */
      }
    };
  }, [passkeySupported, fetchPasskeyOptions, verifyPasskeyAssertion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (res.ok) {
        onLoginSuccess(data.user);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error');
    }
    setLoading(false);
  };

  const handlePasskeyLogin = async () => {
    setError('');
    if (!passkeySupported) {
      setError('Passkeys are not supported in this browser');
      return;
    }

    setPasskeyLoading(true);
    try {
      try {
        WebAuthnAbortService.cancelCeremony();
      } catch {
        /* ignore */
      }

      // Discoverable passkey: no username — browser shows account picker
      const { attemptId, optionsJSON } = await fetchPasskeyOptions();
      const assertion = await startAuthentication({ optionsJSON });
      await verifyPasskeyAssertion(assertion, attemptId);
    } catch (err: unknown) {
      const name = err && typeof err === 'object' && 'name' in err ? String((err as { name: string }).name) : '';
      if (name === 'NotAllowedError' || name === 'AbortError') {
        setError('Passkey sign-in was cancelled');
      } else {
        setError(err instanceof Error ? err.message : 'Passkey login failed');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="absolute inset-0">
        <div className="absolute -top-20 -left-20 w-96 h-96 bg-purple-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-1"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-2"></div>
        <div className="absolute bottom-0 left-10 w-96 h-96 bg-pink-300 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob-3"></div>
        <div className="absolute top-1/3 left-1/2 w-96 h-96 bg-indigo-300 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob-4"></div>
        <div className="absolute bottom-10 right-20 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-25 animate-blob-5"></div>
      </div>

      <div className="relative z-10 w-full max-w-xl mx-auto px-6">
        <div className="relative backdrop-blur-3xl bg-white/25 border border-white/40 rounded-3xl shadow-2xl shadow-indigo-200/50 p-10 transform transition-all duration-300 hover:shadow-3xl before:absolute before:inset-0 before:rounded-3xl before:bg-gradient-to-br before:from-white/40 before:to-white/10 before:pointer-events-none">
          <div className="relative z-10 flex flex-col items-center mb-8">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-blue-500 rounded-2xl blur-xl opacity-40 animate-pulse"></div>
              <div className="relative bg-white/90 backdrop-blur-xl p-4 rounded-2xl border border-white/60 shadow-lg">
                <img
                  src="https://webfiles.remapp.ae/web_remapp_ae/hjas-1-1.png"
                  alt="REM Clients Logo"
                  className="h-16 w-auto"
                />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2 tracking-tight drop-shadow-sm">Welcome Back</h1>
            <p className="text-indigo-700 text-sm font-semibold drop-shadow-sm">
              {passkeySupported ? 'Sign in faster with your passkey' : 'Sign in to access your account'}
            </p>
          </div>

          <div className="relative z-10 space-y-5">
            {/* Primary: Passkey */}
            {passkeySupported && (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={handlePasskeyLogin}
                  disabled={loading || passkeyLoading}
                  autoFocus
                  className="group relative w-full py-4 px-6 bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:shadow-indigo-500/50 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
                >
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <span className="relative flex items-center justify-center gap-2.5 text-base">
                    {passkeyLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Waiting for passkey...
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                        Sign in with passkey
                      </>
                    )}
                  </span>
                </button>
                <p className="text-center text-xs text-indigo-600/80 font-medium">
                  Face ID, Touch ID, or Windows Hello — no password needed
                </p>
              </div>
            )}

            {error && !showPasswordForm && (
              <div className="flex items-center gap-2 p-3 bg-red-100/60 backdrop-blur-xl border-2 border-red-300/70 rounded-xl animate-shake shadow-sm">
                <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-800 text-sm font-semibold">{error}</span>
              </div>
            )}

            {/* Always present for Conditional UI autofill when password form is collapsed */}
            {passkeySupported && !showPasswordForm && (
              <input
                type="text"
                name="username"
                autoComplete="username webauthn"
                value={username}
                onChange={e => setUsername(e.target.value)}
                className="sr-only"
                tabIndex={-1}
                aria-hidden="true"
              />
            )}

            {/* Secondary: Password (collapsed by default when passkeys work) */}
            {passkeySupported && !showPasswordForm ? (
              <div className="pt-1">
                <div className="relative flex items-center gap-3 mb-3">
                  <div className="flex-1 h-px bg-indigo-200/70" />
                  <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">or</span>
                  <div className="flex-1 h-px bg-indigo-200/70" />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setError('');
                    setShowPasswordForm(true);
                  }}
                  disabled={passkeyLoading}
                  className="w-full py-3 px-6 text-indigo-700 font-semibold rounded-xl border-2 border-transparent hover:border-indigo-200 hover:bg-white/40 transition-all duration-200 disabled:opacity-60"
                >
                  Use password instead
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {passkeySupported && (
                  <div className="relative flex items-center gap-3">
                    <div className="flex-1 h-px bg-indigo-200/70" />
                    <span className="text-xs font-semibold text-indigo-500 uppercase tracking-wide">password</span>
                    <div className="flex-1 h-px bg-indigo-200/70" />
                  </div>
                )}

                <div className="relative">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none z-10">
                      <svg className="w-6 h-6 text-indigo-700 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="username"
                      value={username}
                      onChange={e => setUsername(e.target.value)}
                      onFocus={() => setFocusedInput('username')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="Username"
                      autoComplete="username webauthn"
                      className="w-full pl-12 pr-4 py-3.5 bg-white/40 backdrop-blur-xl border-2 border-white/50 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-indigo-400 focus:bg-white/50 transition-all duration-300 shadow-sm font-medium"
                      autoFocus={!passkeySupported}
                      required
                    />
                  </div>
                  {(focusedInput === 'username' || username) && (
                    <label className="absolute -top-2.5 left-3 px-2 text-xs font-bold text-indigo-700 bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
                      Username
                    </label>
                  )}
                </div>

                <div className="relative">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none z-10">
                      <svg className="w-6 h-6 text-indigo-700 drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      onFocus={() => setFocusedInput('password')}
                      onBlur={() => setFocusedInput(null)}
                      placeholder="Password"
                      autoComplete="current-password"
                      className="w-full pl-12 pr-12 py-3.5 bg-white/40 backdrop-blur-xl border-2 border-white/50 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:border-indigo-400 focus:bg-white/50 transition-all duration-300 shadow-sm font-medium"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-indigo-700 hover:text-indigo-800 transition-colors z-10 drop-shadow-sm"
                    >
                      {showPassword ? (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {(focusedInput === 'password' || password) && (
                    <label className="absolute -top-2.5 left-3 px-2 text-xs font-bold text-indigo-700 bg-white/90 backdrop-blur-sm rounded-md shadow-sm">
                      Password
                    </label>
                  )}
                </div>

                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-100/60 backdrop-blur-xl border-2 border-red-300/70 rounded-xl animate-shake shadow-sm">
                    <svg className="w-5 h-5 text-red-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-red-800 text-sm font-semibold">{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || passkeyLoading}
                  className={`w-full py-3.5 px-6 font-semibold rounded-xl transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed ${
                    passkeySupported
                      ? 'bg-white/50 backdrop-blur-xl border-2 border-indigo-200 text-indigo-800 shadow-sm hover:bg-white/70 hover:border-indigo-300'
                      : 'group relative bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 text-white shadow-lg hover:shadow-xl hover:shadow-indigo-500/50 overflow-hidden'
                  }`}
                >
                  <span className="relative flex items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Logging in...
                      </>
                    ) : (
                      'Sign in with password'
                    )}
                  </span>
                </button>

                {passkeySupported && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowPasswordForm(false);
                      setError('');
                    }}
                    className="w-full text-sm text-indigo-600 hover:text-indigo-800 font-medium py-1"
                  >
                    Back to passkey
                  </button>
                )}
              </form>
            )}
          </div>

          <div className="relative z-10 mt-6 text-center">
            <p className="text-gray-700 text-xs font-semibold flex items-center justify-center gap-1 drop-shadow-sm">
              <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Secured with end-to-end encryption
            </p>
          </div>
        </div>

        <div className="absolute -top-6 -left-6 w-32 h-32 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
      </div>

      <style>{`
        @keyframes blob1 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(400px, 200px) scale(1.2); }
          50% { transform: translate(200px, 400px) scale(0.9); }
          75% { transform: translate(-100px, 300px) scale(1.1); }
        }
        @keyframes blob2 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(-300px, 150px) scale(0.9); }
          50% { transform: translate(-500px, 350px) scale(1.15); }
          75% { transform: translate(-200px, 500px) scale(1); }
        }
        @keyframes blob3 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(300px, -200px) scale(1.1); }
          50% { transform: translate(600px, -100px) scale(0.95); }
          75% { transform: translate(150px, -300px) scale(1.05); }
        }
        @keyframes blob4 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(-400px, -250px) scale(1.15); }
          50% { transform: translate(-100px, -400px) scale(0.9); }
          75% { transform: translate(-300px, -150px) scale(1.1); }
        }
        @keyframes blob5 {
          0%, 100% { transform: translate(0px, 0px) scale(1); }
          25% { transform: translate(250px, 300px) scale(0.95); }
          50% { transform: translate(-200px, 200px) scale(1.2); }
          75% { transform: translate(100px, -150px) scale(1); }
        }
        .animate-blob-1 { animation: blob1 20s ease-in-out infinite; }
        .animate-blob-2 { animation: blob2 18s ease-in-out infinite; animation-delay: -5s; }
        .animate-blob-3 { animation: blob3 22s ease-in-out infinite; animation-delay: -10s; }
        .animate-blob-4 { animation: blob4 25s ease-in-out infinite; animation-delay: -15s; }
        .animate-blob-5 { animation: blob5 19s ease-in-out infinite; animation-delay: -8s; }
        .animation-delay-2000 { animation-delay: 2s; }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake { animation: shake 0.5s; }
      `}</style>
    </div>
  );
};

export default LoginForm;
